/**
 * 电商详情图 · 上游商品图解析（多模态优先，失败则文本推断）
 */

import { buildEcomUpstreamImageVisionMessages } from '@/utils/ecomDetailNode'
import {
  getEcomTextInferFromRefsSystem,
  normalizeEcomDetailOutputLanguage
} from '@/utils/ecomDetailLanguage'

/** 依次尝试的方舟视觉模型（须在控制台已开通；也可在节点填写 visionModel 置顶） */
export const ECOM_VISION_MODEL_CANDIDATES = [
  'doubao-1-5-vision-pro-32k-250115',
  'doubao-1-5-vision-lite-32k-250115',
  'doubao-1-5-thinking-vision-pro-32k-250115',
  'doubao-1-5-pro-32k-250115',
  'doubao-seed-1-6-vision-250815'
]

/** @deprecated 使用 getEcomTextInferFromRefsSystem(outputLanguage) */
export const ECOM_TEXT_INFER_FROM_REFS_SYSTEM = getEcomTextInferFromRefsSystem('zh')

export function isVolcVisionUnsupportedError(err) {
  const m = String((err && err.message) || err || '').toLowerCase()
  return (
    m.includes('do not support image') ||
    m.includes('not support image') ||
    m.includes('image input') ||
    m.includes('image_url') ||
    m.includes('unknown variant')
  )
}

function visionModelTryOrder(override) {
  const out = []
  const o = String(override || '').trim()
  if (o) out.push(o)
  for (const id of ECOM_VISION_MODEL_CANDIDATES) {
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/**
 * @param {{ imageUrls: string[], textHints?: string, visionModel?: string, chat: Function }} opts
 * chat(model, messages) => Promise<string>
 */
export async function tryEcomVisionImageAnalysis(opts) {
  const urls = (opts && opts.imageUrls) || []
  const chat = opts && opts.chat
  if (!chat || typeof chat !== 'function' || !urls.length) {
    return { text: '', method: 'none', model: '' }
  }
  const messages = buildEcomUpstreamImageVisionMessages(
    urls,
    opts.textHints,
    opts.outputLanguage
  )
  if (!messages) return { text: '', method: 'none', model: '' }

  const models = visionModelTryOrder(opts.visionModel)
  let lastErr = null
  for (let i = 0; i < models.length; i++) {
    const model = models[i]
    try {
      const raw = await chat(model, messages)
      const text = String(raw || '').trim()
      if (text) return { text, method: 'vision', model }
    } catch (e) {
      lastErr = e
      if (!isVolcVisionUnsupportedError(e)) throw e
    }
  }
  return { text: '', method: 'vision_failed', model: '', error: lastErr }
}

/**
 * @param {{ imageUrls: string[], upstreamMeta: string, textHints?: string, chat: Function, model: string, baseUrl?: string }} opts
 */
export async function inferEcomProductAnalysisFromText(opts) {
  const chat = opts && opts.chat
  const model = opts && opts.model ? String(opts.model) : 'deepseek-v4-flash'
  const urls = (opts && opts.imageUrls) || []
  const meta = String((opts && opts.upstreamMeta) || '').trim()
  const hints = String((opts && opts.textHints) || '').trim()
  const urlBlock =
    urls.length > 0
      ? `【已连接参考图 ${urls.length} 张，出图时将传入 Seedream 对齐外观】\n${urls
          .map((u, i) => `${i + 1}. ${u}`)
          .join('\n')}`
      : ''
  const user = [hints, meta, urlBlock].filter(Boolean).join('\n\n') || '请根据电商详情图需求给出商品分析。'
  const lang = normalizeEcomDetailOutputLanguage(opts && opts.outputLanguage)
  const raw = await chat(
    model,
    [
      { role: 'system', content: getEcomTextInferFromRefsSystem(lang) },
      { role: 'user', content: user }
    ],
    opts && opts.baseUrl
  )
  return String(raw || '').trim()
}
